import {
  Button,
  Box,
  DialogContent,
  DialogActions,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  FormHelperText,
} from "@mui/material";
import React, { FC, useState } from "react";
import {
  ENotificationChannel,
  IDadosDispositivo,
  IFCMSendResponse,
  INotification,
  IPrepareNotification,
  schemaTesteNotificacao,
  TTesteNotificacao,
} from "@deltasge/marauders-map";
import { NotificationTypes } from "interfaces";
import { useAppSelector } from "store";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApplyProps } from "hooks/useApplyProps";
import { format } from "date-fns";
import { api, apiPush } from "configs";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { Form } from "assets/styleds";
import { LoadingButton } from "@mui/lab";

interface DialogTestePushProps {
  onClose: () => void;
  dadosDispositivo?: IDadosDispositivo;
  permission: NotificationTypes;
  token?: string;
  requestPushToken: () => void;
}

export const DialogTestePush: FC<DialogTestePushProps> = ({
  onClose,
  token,
  dadosDispositivo,
  permission,
  requestPushToken,
}) => {
  const somenteEnvioDireto =
    permission == "granted" &&
    (!dadosDispositivo || !dadosDispositivo.notTodas);

  const podeEnviar = token && permission == "granted";

  const [enviando, setEnviando] = useState(false);
  const [requestNewPushToken, setRequestNewPushToken] = useState(false);

  const {
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(schemaTesteNotificacao),
    defaultValues: {
      title: "Titulo da notificação",
      body: "Corpo da notificação",
      to: usuario?.deltaId ? [usuario.deltaId] : [],
      enviarParaMimSemFila: somenteEnvioDireto,
    },
  });

  const applayProps = useApplyProps({
    register,
    errors,
    schema: schemaTesteNotificacao,
  });

  const enviarParaMimSemFila = watch("enviarParaMimSemFila");

  const onSubmit = async (data: TTesteNotificacao) => {
    try {
      if (!usuario) {
        throw new Error("Não foi possível encontrar o usuário autenticado");
      }
      setEnviando(true);

      if (data.enviarParaMimSemFila) {
        const body: INotification = {
          pushTokens: [token],
          title: data.title,
          body: data.body,
          data: {
            channel: ENotificationChannel.conversas,
            data: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            dataNotificacao: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            descricao: data.body,
            escolaId: usuario.escolaId,
            modulo: "mensagens",
            remetente: usuario.displayName || usuario.nome,
            titulo: data.title,
            toId: usuario.deltaId,
            fromId: usuario.deltaId,
          },
        };
        const {
          data: [result],
        } = await apiPush.post<IFCMSendResponse[]>("/api/notifications", body);
        if (result.error) {
          if (
            result.error.code == "messaging/registration-token-not-registered"
          ) {
            setRequestNewPushToken(true);
          }
          throw new Error(
            result.error?.message || "Erro ao enviar notificação"
          );
        }
      } else {
        if (!data.to || !data.to.length) {
          throw new Error("Nenhum destinatário informado.");
        }
        const notifications: IPrepareNotification[] = data.to.map((dest) => {
          return {
            body: data.body,
            title: data.title,
            toIds: [dest],
            to: [],
            fromId: usuario.deltaId,
            data: {
              channel: ENotificationChannel.conversas,
              data: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
              dataNotificacao: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
              descricao: data.body,
              escolaId: usuario.escolaId,
              modulo: "mensagens",
              remetente: usuario.displayName || usuario.nome,
              titulo: data.title,
              toId: dest,
              fromId: usuario.deltaId,
            },
          };
        });
        await api.post("notificacao", notifications);
      }

      snack.success("Notificações enviada com sucesso!");
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Box sx={{ height: "100%", overflow: "auto", width: "100%" }}>
        <DialogContent dividers>
          <Form onSubmit={handleSubmit(onSubmit)} id="send-notification">
            <Grid container spacing={2}>
              {requestNewPushToken && (
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    action={
                      <Button
                        variant="contained"
                        disableElevation
                        size="small"
                        onClick={() => {
                          requestPushToken();
                          onClose();
                        }}
                      >
                        Solicitar novo push token
                      </Button>
                    }
                  >
                    Seu dispositivo não está cadastrado. Clique no botão
                    &ldquo;Solicitar novo push token&rdquo; para cadastrar e
                    receber as notificações.
                  </Alert>
                </Grid>
              )}
              {permission == "granted" &&
                (!dadosDispositivo || !dadosDispositivo.notTodas) && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Você silenciou as notificações dentro do aplicativo. Faça
                      somente o envio direto.
                      <br />O envio direto ignora as configurações de
                      silenciamento do dispositivo.
                    </Alert>
                  </Grid>
                )}
              {token && (
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enviarParaMimSemFila}
                          disabled={!token || somenteEnvioDireto}
                          onChange={() => {
                            setValue(
                              "enviarParaMimSemFila",
                              !enviarParaMimSemFila
                            );
                          }}
                        />
                      }
                      label="Enviar direto"
                    />
                  </FormGroup>
                  <FormHelperText>
                    Não consulta o banco de dados para buscar outros
                    dispositivos, portanto somente este recebera a notificação.
                  </FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField {...applayProps("title", schemaTesteNotificacao)} />
              </Grid>
              <Grid item xs={12}>
                <TextField {...applayProps("body", schemaTesteNotificacao)} />
              </Grid>
            </Grid>
          </Form>
        </DialogContent>
      </Box>
      <DialogActions>
        <Button size="small" variant="text" color="inherit" onClick={onClose}>
          Fechar
        </Button>
        <LoadingButton
          size="small"
          variant="text"
          type="submit"
          form="send-notification"
          loading={enviando}
          disabled={!podeEnviar}
        >
          Enviar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
